/**
 * lib/termDates.ts
 * ------------------------------------------------------------------
 * Single source of truth for term dates + reopen dates. Resolves from the
 * admin-editable TermCalendar (reportSchoolSettings/termCalendar) when set,
 * falling back to the hard-coded lib/school.config values so nothing is ever
 * blank. Pure — the caller supplies the loaded calendar (from DataContext).
 * ------------------------------------------------------------------ */
import { termDates as configTermDates, schoolReopens as configReopens } from '@/lib/school.config';
import type { TermCalendarDocument, TermCalendarEntry } from '@/types';

/** Built-in ISO term dates (LKC 2026) — first-run defaults for the Term Dates
 *  editor AND the fallback date range used to scope term attendance when the
 *  calendar hasn't been saved yet. */
export const TERM_DATE_DEFAULTS: Record<string, TermCalendarEntry> = {
  Term1: { start: '2026-01-13', end: '2026-04-17', reopens: '2026-05-19' },
  Term2: { start: '2026-05-19', end: '2026-08-14', reopens: '2026-09-14' },
  Term3: { start: '2026-09-15', end: '2026-12-09', reopens: '2027-01-13' },
};

/**
 * Is a date inside a school term (first to last day, inclusive)?
 *
 * Term ranges come from the term calendar where it has them, else the
 * built-in dates above. A date in a year that neither covers is treated as
 * IN term: without dates the question cannot be answered, and the safe
 * reading for clocking is the ordinary working day.
 */
export function isInTerm(date: string, cal?: TermCalendarDocument | null): boolean {
  const year = date.slice(0, 4);
  const ranges: { start: string; end: string }[] = [];
  for (const t of Object.keys({ ...TERM_DATE_DEFAULTS, ...(cal?.terms ?? {}) })) {
    const r = resolveTermRange(t, cal);
    if (r) ranges.push(r);
  }
  const thisYear = ranges.filter((r) => r.start.slice(0, 4) === year || r.end.slice(0, 4) === year);
  if (thisYear.length === 0) return true;
  return thisYear.some((r) => r.start <= date && date <= r.end);
}

/** The [start, end] ISO range for a term — calendar first, built-in fallback. */
export function resolveTermRange(term: string, cal?: TermCalendarDocument | null): { start: string; end: string } | null {
  const e = cal?.terms?.[term];
  if (e?.start && e?.end) return { start: e.start, end: e.end };
  const d = TERM_DATE_DEFAULTS[term];
  return d ? { start: d.start, end: d.end } : null;
}

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function parts(iso?: string): { d: number; mon: string; y: string } | null {
  if (!iso) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return null;
  const mi = Number(m[2]) - 1;
  if (mi < 0 || mi > 11) return null;
  return { d: Number(m[3]), mon: MONTHS[mi], y: m[1] };
}

/** "14 September 2026" from an ISO date; '' if unparseable/empty. */
export function formatLongDate(iso?: string): string {
  const p = parts(iso);
  return p ? `${p.d} ${p.mon} ${p.y}` : (iso ?? '');
}

/** "13 January – 17 April" from two ISO dates (year omitted, config style). */
export function formatDateRange(start?: string, end?: string): string {
  const a = parts(start), b = parts(end);
  if (!a || !b) return '';
  return `${a.d} ${a.mon} – ${b.d} ${b.mon}`;
}

/** The term's date-range string — calendar first, config fallback. */
export function resolveTermDates(term: string, cal?: TermCalendarDocument | null): string {
  const e = cal?.terms?.[term];
  if (e?.start && e?.end) return formatDateRange(e.start, e.end);
  return configTermDates(term);
}

/** The "school reopens" date for a term's report — calendar first, config fallback. */
export function resolveSchoolReopens(term: string, cal?: TermCalendarDocument | null): string {
  const e = cal?.terms?.[term];
  if (e?.reopens) return formatLongDate(e.reopens);
  return configReopens(term);
}
